import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { ChevronLeft, Calendar, Loader, Trophy, ChevronRight, Zap, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`;
const spin   = keyframes`100%{transform:rotate(360deg)}`;

const Page = styled.div`
  display:flex;flex-direction:column;height:calc(100vh - 64px);
  background:#080a12;color:#fff;font-family:'Inter',sans-serif;
`;
const PageHeader = styled.div`
  padding:16px 20px;border-bottom:1px solid #16182a;background:#0c0e1a;
  display:flex;align-items:center;justify-content:space-between;
`;
const BackBtn = styled(Link)`
  color:#888;display:flex;align-items:center;text-decoration:none;font-size:14px;
  &:hover{color:#fb923c}
`;
const Title = styled.h2`
  font-size:18px;margin:0;display:flex;align-items:center;gap:8px;
  .badge{font-size:10px;background:linear-gradient(135deg,#f97316,#ea580c);padding:2px 8px;border-radius:10px;font-weight:700;}
`;
const Layout = styled.div`
  display:flex;flex:1;overflow:hidden;
  @media(max-width:768px){flex-direction:column;overflow-y:auto}
`;
const Sidebar = styled.div`
  width:300px;border-right:1px solid #16182a;display:flex;flex-direction:column;
  background:#0a0c18;
  @media(max-width:768px){width:100%;border-right:none;display:${p => p.$hiddenOnMobile ? 'none' : 'flex'};}
`;
const ListHeader = styled.div`
  padding:12px 16px;font-size:12px;color:#4b5563;border-bottom:1px solid #16182a;
  text-transform:uppercase;letter-spacing:.5px;
`;
const SnapshotList    = styled.div`flex:1;overflow-y:auto;position:relative`;
const LoadMoreSentinel = styled.div`height:40px;display:flex;align-items:center;justify-content:center;color:#4b5563;font-size:12px`;
const SnapshotItem = styled.div`
  padding:14px 16px;border-bottom:1px solid #10121f;cursor:pointer;
  background:${p => p.$active ? 'rgba(249,115,22,.1)' : 'transparent'};
  border-left:3px solid ${p => p.$active ? '#f97316' : 'transparent'};
  &:hover{background:${p => p.$active ? 'rgba(249,115,22,.1)' : 'rgba(249,115,22,.04)'}}
`;
const SnapTitle = styled.div`font-weight:600;font-size:13px;margin-bottom:3px;color:${p => p.$active ? '#fb923c' : '#ccc'};`;
const SnapMeta  = styled.div`font-size:11px;color:#4b5563;display:flex;gap:10px;flex-wrap:wrap;`;
const Detail = styled.div`
  flex:1;overflow-y:auto;padding:24px;
  @media(max-width:768px){padding:16px;display:${p => p.$visibleOnMobile ? 'block' : 'none'};}
`;
const DetailTitle = styled.div`font-size:22px;font-weight:700;color:#fb923c;margin-bottom:4px;`;
const SectionTitle = styled.h3`
  font-size:13px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.7px;
  margin:24px 0 14px;display:flex;align-items:center;gap:8px;
`;
const GRID_H = '40px 160px 70px 70px 55px 70px 65px 70px 30px';
const RankRow = styled.div`
  display:grid;grid-template-columns:${GRID_H};
  gap:10px;padding:12px 14px;border-radius:10px;margin-bottom:6px;
  background:${p => p.$momentum ? 'rgba(249,115,22,.06)' : 'rgba(168,85,247,.04)'};
  border:1px solid ${p => p.$momentum ? 'rgba(249,115,22,.2)' : 'rgba(168,85,247,.1)'};
  cursor:pointer;align-items:center;font-size:13px;animation:${fadeIn} .25s;
  min-width:720px;
  &:hover{background:${p => p.$momentum ? 'rgba(249,115,22,.12)' : 'rgba(168,85,247,.1)'};border-color:${p => p.$momentum ? 'rgba(249,115,22,.4)' : 'rgba(168,85,247,.3)'};}
`;
const RankNum = styled.div`
  font-weight:700;font-size:14px;text-align:center;
  color:${p => p.$rank <= 3 ? '#fbbf24' : p.$rank <= 10 ? '#fb923c' : '#64748b'};
`;
const ScoreNum = styled.div`
  font-weight:700;text-align:right;
  color:${p => p.$score >= 70 ? '#fbbf24' : p.$score >= 50 ? '#fb923c' : p.$score >= 30 ? '#94a3b8' : '#475569'};
`;
const PassPill = styled.div`
  display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:5px;font-size:11px;font-weight:600;
  ${p => p.$full ? 'background:rgba(34,197,94,.15);color:#4ade80;border:1px solid rgba(34,197,94,.25);'
    : p.$high ? 'background:rgba(249,115,22,.12);color:#fb923c;border:1px solid rgba(249,115,22,.2);'
    : p.$mid  ? 'background:rgba(234,179,8,.10);color:#eab308;border:1px solid rgba(234,179,8,.18);'
    : 'background:rgba(255,255,255,.04);color:#64748b;border:1px solid rgba(255,255,255,.08);'}
`;
const PerfBadge = styled.div`
  font-size:11px;padding:3px 7px;border-radius:5px;font-weight:600;display:inline-block;margin-left:6px;
`;
const EmptyMsg = styled.div`
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  height:100%;color:#374151;gap:12px;font-size:14px;padding:40px;text-align:center;
  @media(max-width:768px){display:${p => p.$hideOnMobile ? 'none' : 'flex'};}
`;
const Spinner  = styled(Loader)`animation:${spin} 1s linear infinite;margin-bottom:16px;`;
const SyncingOverlay = styled.div`
  position:fixed;top:0;left:0;width:100%;height:100%;
  background:rgba(0,0,0,0.85);display:flex;flex-direction:column;
  align-items:center;justify-content:center;z-index:9999;backdrop-filter:blur(4px);
`;
const ComponentsSection = styled.div`
  margin-top:8px;padding:10px 12px;border-radius:6px;
  background:rgba(249,115,22,.04);border:1px solid rgba(249,115,22,.12);
`;
const ComponentsGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:8px;margin-top:8px;
`;
const ComponentItem = styled.div`
  padding:8px;border-radius:4px;font-size:11px;
  background:${p => p.$momentum ? 'rgba(249,115,22,.1)' : 'rgba(168,85,247,.08)'};
  .label{color:#64748b;font-weight:600;text-transform:uppercase;letter-spacing:.3px;}
  .score{color:${p => p.$passing ? '#4ade80' : p.$momentum ? '#fb923c' : '#94a3b8'};font-weight:700;font-size:13px;margin-top:2px;}
  .details{color:#4b5563;font-size:10px;margin-top:2px;}
`;
const ToggleComponentsBtn = styled.button`
  background:none;border:none;color:#fb923c;cursor:pointer;font-size:12px;
  display:flex;align-items:center;gap:4px;padding:0;font-weight:600;
  &:hover{color:#f97316;}
`;
const TableWrapper = styled.div`width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;`;
const MoodBadge = styled.span`
  font-size:11px;font-weight:700;padding:2px 8px;border-radius:6px;
  background:${p => p.$mood === 'MOMENTUM' ? 'rgba(249,115,22,.15)' : p.$mood === 'HOT' ? 'rgba(239,68,68,.1)' : 'rgba(100,116,139,.08)'};
  color:${p => p.$mood === 'MOMENTUM' ? '#f97316' : p.$mood === 'HOT' ? '#ef4444' : '#94a3b8'};
  border:1px solid ${p => p.$mood === 'MOMENTUM' ? 'rgba(249,115,22,.3)' : p.$mood === 'HOT' ? 'rgba(239,68,68,.2)' : 'rgba(100,116,139,.15)'};
`;

export default function LivermoreV42HistoryPage() {
    const [snapshots,     setSnapshots]     = useState([]);
    const [selectedSnap,  setSelectedSnap]  = useState(null);
    const [details,       setDetails]       = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [loading,       setLoading]       = useState(true);
    const [loadingMore,   setLoadingMore]   = useState(false);
    const [hasMore,       setHasMore]       = useState(false);
    const [cursor,        setCursor]        = useState(null);
    const [isMobile,      setIsMobile]      = useState(window.innerWidth <= 768);
    const [isSyncing,     setIsSyncing]     = useState(false);
    const [expandedItems, setExpandedItems] = useState(new Set());
    const sentinelRef = useRef(null);
    const observerRef = useRef(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        apiService.getLivermoreV42History().then(res => {
            const items = res?.items || [];
            setSnapshots(items);
            setHasMore(res?.hasMore || false);
            setCursor(items.length > 0 ? items[items.length - 1].id : null);
            if (items.length > 0 && window.innerWidth > 768) handleSelect(items[0]);
        }).catch(console.error).finally(() => setLoading(false));
    }, []); // handleSelect는 초기 1회만 실행

    const loadMore = useCallback(async () => {
        if (loadingMore || !hasMore || !cursor) return;
        setLoadingMore(true);
        try {
            const res   = await apiService.getLivermoreV42History(cursor);
            const items = res?.items || [];
            setSnapshots(prev => [...prev, ...items]);
            setHasMore(res?.hasMore || false);
            setCursor(items.length > 0 ? items[items.length - 1].id : null);
        } catch (e) { console.error(e); }
        finally { setLoadingMore(false); }
    }, [loadingMore, hasMore, cursor]);

    useEffect(() => {
        if (observerRef.current) observerRef.current.disconnect();
        observerRef.current = new IntersectionObserver(
            entries => { if (entries[0].isIntersecting) loadMore(); },
            { threshold: 0.1 }
        );
        if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
        return () => observerRef.current?.disconnect();
    }, [loadMore]);

    const handleSelect = async (snap) => {
        setSelectedSnap(snap);
        setDetailsLoading(true);
        setDetails(null);
        try {
            const data = await apiService.getLivermoreV42HistoryDetail(snap.id);
            setDetails(data);

            const needsCalc = data.ranked?.some(
                r => !r.performance || r.performance.todayMax === '-' || r.performance.nextDayMax === '-'
            );
            if (data.ranked?.length > 0 && needsCalc) {
                const createdAt = new Date(data.createdAt);
                const nextDay   = new Date(createdAt);
                nextDay.setDate(nextDay.getDate() + 1);
                nextDay.setHours(15, 30, 0, 0);
                if (new Date() > nextDay) {
                    const updated = await apiService.calculateLivermoreV42Performance(snap.id);
                    if (updated.success) setDetails(prev => ({ ...prev, ranked: updated.ranked }));
                }
            }
        } catch (e) { console.error(e); }
        finally { setDetailsLoading(false); }
    };

    const handleRecalculate = async () => {
        if (!selectedSnap || !window.confirm('현재 성과를 다시 계산하시겠습니까?')) return;
        setIsSyncing(true);
        try {
            const updated = await apiService.calculateLivermoreV42Performance(selectedSnap.id, true);
            if (updated.success) {
                setDetails(prev => ({ ...prev, ranked: updated.ranked }));
                alert('성과 재계산이 완료되었습니다.');
            }
        } catch (e) { console.error(e); alert('재계산 중 오류가 발생했습니다.'); }
        finally { setIsSyncing(false); }
    };

    const formatTime = (iso) => {
        if (!iso) return '';
        return new Date(iso).toLocaleString('ko-KR', {
            month:'long', day:'numeric', hour:'2-digit', minute:'2-digit', hour12:false
        });
    };
    const formatMarketCap = (item) => {
        const mcap = item.mcap;
        if (!mcap || mcap <= 0) return '-';
        return mcap >= 10000 ? `${(mcap/10000).toFixed(1)}조` : `${mcap}억`;
    };

    const toggleExpandItem = (code, e) => {
        e.stopPropagation();
        const s = new Set(expandedItems);
        s.has(code) ? s.delete(code) : s.add(code);
        setExpandedItems(s);
    };

    const renderComponentsDetail = (item) => {
        if (!item.components) return null;
        const c = item.components;
        const isMomentum = item.isMomentumStock;
        return (
            <ComponentsSection>
                <ToggleComponentsBtn onClick={(e) => toggleExpandItem(item.code, e)}>
                    <ChevronDown size={14} style={{ transform: expandedItems.has(item.code) ? 'rotate(180deg)' : 'rotate(0deg)', transition: '.2s' }} />
                    항목별 점수 {isMomentum ? '(⚡ Momentum Track)' : '(Flat Base Track)'}
                </ToggleComponentsBtn>
                {expandedItems.has(item.code) && (
                    <ComponentsGrid>
                        {/* 모멘텀 트랙 전용 항목 */}
                        {isMomentum && c.acceleration && (
                            <ComponentItem $momentum={true} $passing={c.acceleration.score > 0}>
                                <div className="label">⚡가속도(30D)</div>
                                <div className="score">{c.acceleration.score?.toFixed(0)||0}/{c.acceleration.max||25}</div>
                                <div className="details">{item.priceChange30D || '-'}</div>
                            </ComponentItem>
                        )}
                        {isMomentum && c.momentumVolSurge && (
                            <ComponentItem $momentum={true} $passing={c.momentumVolSurge.score > 0}>
                                <div className="label">⚡모멘텀거래량</div>
                                <div className="score">{c.momentumVolSurge.score?.toFixed(0)||0}/{c.momentumVolSurge.max||25}</div>
                                <div className="details">{c.momentumVolSurge.volSurge?.toFixed(2)||'-'}x</div>
                            </ComponentItem>
                        )}
                        {isMomentum && c.trValueScore && (
                            <ComponentItem $momentum={true} $passing={c.trValueScore.score > 0}>
                                <div className="label">⚡거래대금</div>
                                <div className="score">{c.trValueScore.score||0}/{c.trValueScore.max||15}</div>
                                <div className="details">
                                    {(c.trValueScore.trValue||0) >= 1_000_000_000_000
                                        ? `${((c.trValueScore.trValue)/1_000_000_000_000).toFixed(1)}조`
                                        : `${Math.floor((c.trValueScore.trValue||0)/100_000_000)}억`}
                                </div>
                            </ComponentItem>
                        )}
                        {/* 공통 항목 */}
                        <ComponentItem $score={c.proximity?.score||0} $passing={c.proximity?.pass}>
                            <div className="label">저항선</div>
                            <div className="score">{c.proximity?.score?.toFixed(0)||0}/{c.proximity?.max||25}</div>
                            <div className="details">{((c.proximity?.value||0)*100).toFixed(1)}%</div>
                        </ComponentItem>
                        <ComponentItem $score={c.uptrend?.score||0} $passing={c.uptrend?.pass}>
                            <div className="label">정배열</div>
                            <div className="score">{c.uptrend?.score||0}/{c.uptrend?.max||15}</div>
                            <div className="details">MA {c.uptrend?.close?.toFixed(0)||'-'}</div>
                        </ComponentItem>
                        <ComponentItem $score={c.closePosition?.score||0} $passing={c.closePosition?.pass}>
                            <div className="label">종가위치</div>
                            <div className="score">{c.closePosition?.score?.toFixed(0)||0}/{c.closePosition?.max||10}</div>
                            <div className="details">{((c.closePosition?.value||0)*100).toFixed(0)}%</div>
                        </ComponentItem>
                        {/* Flat Base 전용 항목 (모멘텀 종목이면 회색으로 참고용 표시) */}
                        {!isMomentum && (
                            <>
                                <ComponentItem $score={c.volatility?.score||0} $passing={c.volatility?.pass}>
                                    <div className="label">변동성</div>
                                    <div className="score">{c.volatility?.score?.toFixed(0)||0}/{c.volatility?.max||15}</div>
                                    <div className="details">{((c.volatility?.value||0)*100).toFixed(1)}%</div>
                                </ComponentItem>
                                <ComponentItem $score={c.volSurge?.score||0} $passing={c.volSurge?.pass}>
                                    <div className="label">거래량</div>
                                    <div className="score">{c.volSurge?.score?.toFixed(0)||0}/{c.volSurge?.max||15}</div>
                                    <div className="details">{c.volSurge?.value?.toFixed(2)||'-'}x</div>
                                </ComponentItem>
                                <ComponentItem $score={c.vcp?.score||0} $passing={c.vcp?.pass}>
                                    <div className="label">VCP</div>
                                    <div className="score">{c.vcp?.score?.toFixed(0)||0}/{c.vcp?.max||15}</div>
                                    <div className="details">{c.vcp?.driedDays||0}일</div>
                                </ComponentItem>
                            </>
                        )}
                        {c.megaVol != null && (
                            <ComponentItem $score={c.megaVol?.score||0} $passing={c.megaVol?.pass}>
                                <div className="label">대형거래대금</div>
                                <div className="score">{c.megaVol?.score||0}/{c.megaVol?.max||10}</div>
                                <div className="details">
                                    {(c.megaVol?.trValue||0) >= 1_000_000_000_000
                                        ? `${(c.megaVol.trValue/1_000_000_000_000).toFixed(1)}조`
                                        : `${Math.floor((c.megaVol?.trValue||0)/100_000_000)}억`}
                                </div>
                            </ComponentItem>
                        )}
                        {c.megaCapBreakout != null && (
                            <ComponentItem $score={c.megaCapBreakout?.score||0} $passing={c.megaCapBreakout?.pass}>
                                <div className="label">대형주ATH</div>
                                <div className="score">{c.megaCapBreakout?.score||0}/{c.megaCapBreakout?.max||20}</div>
                                <div className="details">{c.megaCapBreakout?.isNew52WHigh ? '52W신고가✅' : '❌'}</div>
                            </ComponentItem>
                        )}
                    </ComponentsGrid>
                )}
            </ComponentsSection>
        );
    };

    const mood = details?.marketMood || selectedSnap?.marketMood;
    const moodLabel = mood === 'MOMENTUM' ? '⚡ MOMENTUM' : mood === 'HOT' ? '🔥 HOT' : '😐 NEUTRAL';

    return (
        <Page>
            {isSyncing && (
                <SyncingOverlay>
                    <Spinner size={48} color="#f97316" />
                    <h2 style={{ margin:0, marginBottom:'8px', color:'#fb923c' }}>성과 재계산 중...</h2>
                </SyncingOverlay>
            )}
            <PageHeader>
                <div style={{ display:'flex', alignItems:'center', gap:'15px' }}>
                    {isMobile && selectedSnap ? (
                        <button onClick={() => setSelectedSnap(null)}
                            style={{ background:'none', border:'none', color:'#fb923c', display:'flex', alignItems:'center', padding:0 }}>
                            <ChevronLeft size={20} /> 리스트
                        </button>
                    ) : (
                        <BackBtn to="/labs/livermore-v42"><ChevronLeft size={16} />뒤로</BackBtn>
                    )}
                </div>
                <Title>
                    <Calendar size={18} color="#f97316" />
                    Livermore V4.2 히스토리
                    <span className="badge">V4.2</span>
                </Title>
                <button onClick={handleRecalculate} disabled={isSyncing || !selectedSnap}
                    style={{ background: selectedSnap ? 'rgba(249,115,22,0.2)' : '#16182a',
                        color: selectedSnap ? '#fb923c' : '#4b5563',
                        border: `1px solid ${selectedSnap ? '#f97316' : '#16182a'}`,
                        padding:'6px 14px', borderRadius:'8px', fontSize:'12px',
                        fontWeight:700, cursor:(isSyncing||!selectedSnap) ? 'not-allowed' : 'pointer',
                        display:'flex', alignItems:'center', gap:'8px' }}>
                    {isSyncing ? <Spinner size={12} style={{ margin:0 }} /> : <Zap size={12} />}
                    {isSyncing ? '계산 중...' : '다시 분석하기'}
                </button>
            </PageHeader>

            <Layout>
                <Sidebar $hiddenOnMobile={isMobile && selectedSnap}>
                    <ListHeader>RECORDED SNAPSHOTS — V4.2</ListHeader>
                    <SnapshotList>
                        {loading && <EmptyMsg><Loader size={20} color="#f97316" />로딩 중...</EmptyMsg>}
                        {!loading && snapshots.length === 0 && <EmptyMsg>저장된 기록이 없습니다</EmptyMsg>}
                        {snapshots.map(snap => (
                            <SnapshotItem key={snap.id} $active={selectedSnap?.id === snap.id} onClick={() => handleSelect(snap)}>
                                <SnapTitle $active={selectedSnap?.id === snap.id}>
                                    {formatTime(snap.createdAt)} Analysis
                                </SnapTitle>
                                <SnapMeta>
                                    <MoodBadge $mood={snap.marketMood}>{snap.marketMood === 'MOMENTUM' ? '⚡ MOMENTUM' : snap.marketMood === 'HOT' ? '🔥 HOT' : '😐'}</MoodBadge>
                                    <span>{snap.rankedCount||0}개</span>
                                    {snap.momentumCount > 0 && <span style={{ color:'#f97316', fontWeight:700 }}>⚡{snap.momentumCount}</span>}
                                    {snap.flatBaseCount > 0  && <span style={{ color:'#4ade80', fontWeight:700 }}>📐{snap.flatBaseCount}</span>}
                                    {snap.topScore !== undefined && <span style={{ color:'#fb923c' }}>최고 {snap.topScore?.toFixed?.(0) ?? snap.topScore}</span>}
                                </SnapMeta>
                            </SnapshotItem>
                        ))}
                        <LoadMoreSentinel ref={sentinelRef}>
                            {loadingMore && <Spinner size={14} color="#f97316" />}
                            {!loadingMore && hasMore && <span>스크롤하여 더 보기</span>}
                        </LoadMoreSentinel>
                    </SnapshotList>
                </Sidebar>

                {!selectedSnap ? (
                    <EmptyMsg style={{ flex:1 }} $hideOnMobile={isMobile}>
                        <Calendar size={40} color="#16182a" />
                        <span>히스토리를 선택하세요</span>
                    </EmptyMsg>
                ) : (
                    <Detail $visibleOnMobile={!isMobile || selectedSnap}>
                        <DetailTitle>{formatTime(details?.createdAt || selectedSnap.createdAt)}</DetailTitle>
                        <div style={{ fontSize:'13px', color:'#4b5563', marginBottom:8, display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
                            <MoodBadge $mood={mood}>{moodLabel}</MoodBadge>
                            {details?.momentumCount > 0 && (
                                <span style={{ fontSize:12, color:'#f97316', fontWeight:700 }}>⚡ 모멘텀 {details.momentumCount}개</span>
                            )}
                            {details?.flatBaseCount > 0 && (
                                <span style={{ fontSize:12, color:'#4ade80', fontWeight:700 }}>📐 베이스 {details.flatBaseCount}개</span>
                            )}
                            {details?.weights && (
                                <span style={{ marginLeft:'auto', fontSize:11 }}>
                                    가중치: Leader {details.weights.leader} + Pivot {details.weights.pivot}
                                </span>
                            )}
                        </div>

                        {detailsLoading && <EmptyMsg><Loader size={20} color="#f97316" />분석 로딩 중...</EmptyMsg>}

                        {details && !detailsLoading && (
                            <>
                                <SectionTitle>
                                    <Trophy size={14} color="#f97316" />
                                    Ranked Candidates ({details.ranked?.length||0})
                                </SectionTitle>
                                <TableWrapper>
                                    <div style={{ display:'grid', gridTemplateColumns:GRID_H, gap:10, padding:'0 14px 8px', minWidth:'720px', fontSize:11, color:'#4b5563', textTransform:'uppercase', letterSpacing:'.5px' }}>
                                        <div style={{ textAlign:'center' }}>#</div>
                                        <div>종목</div>
                                        <div style={{ textAlign:'right' }}>총점</div>
                                        <div style={{ textAlign:'right' }}>Leader</div>
                                        <div style={{ textAlign:'right' }}>Pivot</div>
                                        <div style={{ textAlign:'center' }}>30D</div>
                                        <div style={{ textAlign:'right' }}>시가총액</div>
                                        <div style={{ textAlign:'center' }}>통과</div>
                                        <div></div>
                                    </div>

                                    {(details.ranked||[]).map((item) => {
                                        const passRatio = (item.thresholdsPassed||0)/(item.thresholdsTotal||7);
                                        const todayVal  = item.performance?.todayMax;
                                        const nextVal   = item.performance?.nextDayMax;
                                        const isMomentum = item.isMomentumStock;
                                        const effectivePivot = item.effectivePivotScore ?? item.pivotScore;
                                        const change30D = item.priceChange30DRaw ?? 0;

                                        return (
                                            <div key={item.code} style={{ minWidth:'720px' }}>
                                                <RankRow $momentum={isMomentum}
                                                    onClick={() => window.open(`/labs/stock/technical?symbol=${item.code}${item.resistanceLine ? `&pivot=${item.resistanceLine}` : ''}`, '_blank')}>
                                                    <RankNum $rank={item.rank}>{item.rank}</RankNum>
                                                    <div style={{ minWidth:0 }}>
                                                        <div style={{ fontWeight:600, color:'#e2e8f0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                                                            {item.name}
                                                            {isMomentum && <span style={{ marginLeft:6, background:'linear-gradient(135deg,#f97316,#ea580c)', color:'#fff', padding:'1px 5px', borderRadius:3, fontSize:9, fontWeight:700 }}>⚡</span>}
                                                            {item.isTrendStarter && !isMomentum && <span style={{ marginLeft:6, background:'linear-gradient(135deg,#10b981,#059669)', color:'#fff', padding:'1px 5px', borderRadius:3, fontSize:9, fontWeight:700 }}>👑</span>}
                                                        </div>
                                                        <div style={{ fontSize:11, color:'#475569', marginTop:2, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                                                            <span>{item.price?.toLocaleString()}원</span>
                                                            <span style={{ color: item.changeRate >= 0 ? '#ef4444' : '#3b82f6' }}>
                                                                {item.changeRate >= 0 ? '+' : ''}{item.changeRate?.toFixed(2)}%
                                                            </span>
                                                            <span style={{ color: isMomentum ? '#f97316' : '#64748b', fontSize:10, fontWeight:isMomentum ? 700 : 400 }}>
                                                                {item.v31Verdict?.replace('_', ' ')}
                                                            </span>
                                                            {todayVal && todayVal !== '-' && (
                                                                <PerfBadge style={{ background: todayVal.includes('+') ? 'rgba(239,68,68,.08)' : 'rgba(59,130,246,.08)', color: todayVal.includes('+') ? '#ef4444' : '#3b82f6' }}>
                                                                    T: {todayVal}
                                                                </PerfBadge>
                                                            )}
                                                            {nextVal && nextVal !== '-' && nextVal !== todayVal && (
                                                                <PerfBadge style={{ background: nextVal.includes('+') ? 'rgba(239,68,68,.08)' : 'rgba(59,130,246,.08)', color: nextVal.includes('+') ? '#ef4444' : '#3b82f6' }}>
                                                                    T+1: {nextVal}
                                                                </PerfBadge>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <ScoreNum $score={item.totalScore}>{item.totalScore?.toFixed(1)}</ScoreNum>
                                                    <ScoreNum $score={item.leaderScore}>{item.leaderScore?.toFixed(1)}</ScoreNum>
                                                    <ScoreNum $score={effectivePivot}>{effectivePivot?.toFixed(1)}</ScoreNum>
                                                    <div style={{ textAlign:'center', fontSize:11, fontWeight:700,
                                                        color: change30D >= 0.5 ? '#f97316' : change30D >= 0.15 ? '#eab308' : '#94a3b8' }}>
                                                        {item.priceChange30D || '-'}
                                                    </div>
                                                    <div style={{ textAlign:'right', fontSize:12, color:'#94a3b8', fontWeight:600 }}>
                                                        {formatMarketCap(item)}
                                                    </div>
                                                    <div style={{ textAlign:'center' }}>
                                                        <PassPill $full={passRatio >= 1} $high={passRatio >= 0.7 && passRatio < 1} $mid={passRatio >= 0.4 && passRatio < 0.7}>
                                                            {item.thresholdsPassed}/{item.thresholdsTotal}
                                                        </PassPill>
                                                    </div>
                                                    <div style={{ textAlign:'center', color:'#16182a' }}>
                                                        <ChevronRight size={16} />
                                                    </div>
                                                </RankRow>
                                                {renderComponentsDetail(item)}
                                            </div>
                                        );
                                    })}
                                </TableWrapper>
                            </>
                        )}
                    </Detail>
                )}
            </Layout>
        </Page>
    );
}
